/**
 * Alert drill-down logic (OP#1854) -- pure, unit-tested (alertGuide.test.ts).
 *
 * Three jobs, all of them about never leaving an operator at a dead end:
 *
 *  1. WHAT + WHAT TO DO. `alertGuide(kind, deviceKind)` turns a machine alert type
 *     (offline, cpu_high, memory_high, error_surge, cert_expiring, ai_anomaly) into a plain-language
 *     label and a suggested action. The copy is written for a property's on-site staff as much as
 *     for an engineer: no jargon, one concrete first step, and what "done" looks like.
 *
 *  2. EVERY COUNT IS A LINK. `alertsHref` / `offlineDevicesHref` build the one URL each count
 *     links to, so the Customers table, the site Overview and the Health tiles all land on the
 *     same filtered list for the same scope.
 *
 *  3. NEVER FAIL OPEN. `worklistView` decides what the "Needs attention" block may say. "All
 *     clear" is allowed ONLY when the list fetch succeeded, returned nothing, AND the tile count
 *     for the same scope is zero (or unknown because the overview has not loaded). A failed
 *     fetch is an error state; a non-zero count with an empty list is "counted but not listed".
 *     Before this, a 404 became an empty list and the page said "All clear" next to "3 critical".
 */

/** The alert types the controller raises (models/health.py AlertTypeEnum values). */
export type AlertKind =
  | "offline"
  | "cpu_high"
  | "memory_high"
  | "error_surge"
  | "cert_expiring"
  | "ai_anomaly";

export interface AlertGuide {
  /** Short noun phrase for the row title, e.g. "Access point offline". */
  label: string;
  /** One-paragraph suggested action in plain language. */
  action: string;
}

/** Friendly device noun for the derived device kind (models/device.derive_device_kind). */
const DEVICE_NOUN: Record<string, string> = {
  access_point: "Access point",
  switch: "Switch",
  gateway: "Gateway",
};

const GUIDE: Record<AlertKind, AlertGuide> = {
  offline: {
    label: "Device offline",
    action:
      "Check that the device has power and that its network cable is plugged in at both ends. " +
      "For an access point, also check that the switch port it is plugged into is providing " +
      "power (PoE). If it is powered and cabled, unplug it for 10 seconds and plug it back in. " +
      "It reconnects by itself once it can reach Meridian; the alert then clears on its own.",
  },
  cpu_high: {
    label: "Processor busy",
    action:
      "The device has been working above 80% of its processor capacity. Check whether a busy " +
      "period or a recent settings change explains it. If it continues for more than an hour, " +
      "schedule a restart at a quiet time.",
  },
  memory_high: {
    label: "Memory almost full",
    action:
      "The device is using more than 90% of its memory, which often leads to dropped " +
      "connections or a crash. Schedule a restart at a quiet time. If the alert comes back " +
      "after the restart, report it to support with the device's firmware version.",
  },
  error_surge: {
    label: "Unusual number of errors",
    action:
      "The device is reporting many more errors than normal. Open the device and look at its " +
      "recent events: a settings change that did not apply or a cable that keeps disconnecting " +
      "are the usual causes.",
  },
  cert_expiring: {
    label: "Security certificate expiring",
    action:
      "The certificate the device uses to connect securely is about to expire, or already has. " +
      "Renew it while the device is still online: once it expires, the device cannot reconnect " +
      "without someone on site.",
  },
  ai_anomaly: {
    label: "Unusual behaviour detected",
    action:
      "One of the device's measurements moved far outside its normal range. Open the device to " +
      "see which one and compare it with recent changes. No action is needed if the change was " +
      "expected (for example, an event with many guests).",
  },
};

const FALLBACK_ACTION = "Open the device for details.";

/** True when `kind` is one of the alert types this module has copy for. */
export function isKnownAlertKind(kind: string): kind is AlertKind {
  return Object.prototype.hasOwnProperty.call(GUIDE, kind);
}

/**
 * Plain-language label + suggested action for an alert. `deviceKind` sharpens the offline
 * label ("Access point offline" rather than "Device offline"). Unknown kinds degrade to a
 * readable label ("radio_saturation" -> "Radio saturation") and a generic action, never "".
 */
export function alertGuide(kind: string, deviceKind?: string | null): AlertGuide {
  if (!isKnownAlertKind(kind)) {
    const words = (kind || "alert").replace(/_/g, " ").trim();
    return { label: words.charAt(0).toUpperCase() + words.slice(1), action: FALLBACK_ACTION };
  }
  const g = GUIDE[kind];
  if (kind === "offline" && deviceKind && DEVICE_NOUN[deviceKind]) {
    return { ...g, label: `${DEVICE_NOUN[deviceKind]} offline` };
  }
  return g;
}

/** Severity filter values the Alerts page and API accept. */
export type AlertSeverityFilter = "critical" | "warn";

/**
 * URL of the Alerts list for a scope. Site scope when `siteId` is set, else customer scope.
 * Only known, non-empty filters are encoded, in a stable order (so links are comparable).
 */
export function alertsHref(
  orgId: string,
  siteId?: string | null,
  filters: { severity?: AlertSeverityFilter | null; kind?: string | null } = {},
): string {
  const base = siteId ? `/o/${orgId}/s/${siteId}/alerts` : `/o/${orgId}/alerts`;
  const qs = new URLSearchParams();
  if (filters.severity === "critical" || filters.severity === "warn") qs.set("severity", filters.severity);
  if (filters.kind) qs.set("kind", filters.kind);
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

/** URL of the Devices list filtered to offline devices for a scope. */
export function offlineDevicesHref(orgId: string, siteId?: string | null): string {
  return siteId ? `/o/${orgId}/s/${siteId}/devices?status=offline` : `/o/${orgId}/devices?status=offline`;
}

/** Parse ?severity= from the URL; anything else means "no severity filter". */
export function parseSeverity(raw: string | null | undefined): AlertSeverityFilter | null {
  return raw === "critical" || raw === "warn" ? raw : null;
}

/** What the "Needs attention" block may render. See the module docstring, rule 3. */
export type WorklistView = "loading" | "error" | "clear" | "list" | "unlisted";

export function worklistView(input: {
  /** The list query failed (any HTTP error, including 404 -- no more swallowing). */
  isError: boolean;
  /** The list query has not produced data yet. */
  isLoading: boolean;
  /** Rows returned by the list. */
  itemCount: number;
  /** The count shown on the tile for the same scope, or null/undefined when unknown. */
  tileTotal?: number | null;
}): WorklistView {
  if (input.isError) return "error";
  if (input.isLoading) return "loading";
  if (input.itemCount > 0) return "list";
  // Empty list. Only "All clear" when nothing else on screen claims otherwise.
  if (input.tileTotal != null && input.tileTotal > 0) return "unlisted";
  return "clear";
}

/**
 * Compact duration between `fromMs` and `nowMs`: "just now", "12 min", "3 h", "11 d".
 * Floors (never rounds up), so "since 59 minutes" does not read as "1 h".
 */
export function durationLabel(fromMs: number | null, nowMs: number): string {
  if (fromMs === null || Number.isNaN(fromMs)) return "unknown";
  const secs = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs} h`;
  return `${Math.floor(hrs / 24)} d`;
}
